import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';

const ONBOARDING_BACK_MESSAGE =
  'Are you sure you want to go back? Your progress on this page may not be saved!';

const HOST_ONBOARDING_EXCLUDED = [
  '/host/coupons',
  '/host/affiliates',
  '/host/audit',
  '/host/my-bids',
  '/host/myBookings',
];

function isOnboardingPath(pathname: string): boolean {
  if (!pathname.startsWith('/host/')) return false;
  return !HOST_ONBOARDING_EXCLUDED.some((p) => pathname.startsWith(p));
}

/**
 * Hook to show a confirmation popup when user tries to go back (via Back button or browser back)
 * on any host onboarding flow page.
 *
 * @param onBackConfirm - The actual back navigation logic to execute when user confirms (optional for pages without Back button)
 * @returns Wrapped backHandler and BackConfirmModal component
 */
export function useOnboardingBackConfirm(onBackConfirm?: () => void) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [pendingBackFromBrowser, setPendingBackFromBrowser] = useState(false);

  const handleConfirm = useCallback(() => {
    setShowModal(false);
    if (pendingBackFromBrowser) {
      setPendingBackFromBrowser(false);
      navigate(-2);
    } else if (onBackConfirm) {
      onBackConfirm();
    }
  }, [onBackConfirm, pendingBackFromBrowser, navigate]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setPendingBackFromBrowser(false);
    if (typeof window !== 'undefined') {
      window.history.pushState(
        { onboardingBackBlock: true },
        '',
        window.location.href,
      );
    }
  }, []);

  const wrappedBackHandler = useCallback(() => {
    setPendingBackFromBrowser(false);
    setShowModal(true);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname + window.location.search;
      if (isOnboardingPath(path)) {
        setPendingBackFromBrowser(true);
        setShowModal(true);
        window.history.pushState(
          { onboardingBackBlock: true },
          '',
          window.location.href,
        );
      }
    };

    if (typeof window !== 'undefined' && isOnboardingPath(window.location.pathname)) {
      window.history.pushState(
        { onboardingBackBlock: true },
        '',
        window.location.href,
      );
      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
    return undefined;
  }, []);

  const BackConfirmModal = React.createElement(
    Modal,
    {
      title: 'Go back to the previous step?',
      open: showModal,
      onOk: handleConfirm,
      onCancel: handleCancel,
      okText: 'Yes, go back',
      cancelText: 'Stay',
    },
    React.createElement('p', null, ONBOARDING_BACK_MESSAGE),
  );

  return { backHandler: wrappedBackHandler, BackConfirmModal };
}
